/**
 * The follow camera (the follow branch of `cameraGoal` in the dungeon-floors
 * prototype's `dungeon-main.js`): looking north from above and behind the
 * leader, pulled back on narrow (portrait) screens so the party stays
 * readable. In portrait the view looks past the leader by the follow rig's
 * lead (1.4 tiles at full portrait): the camera position, and with it every
 * camera-to-leader ray, is unchanged; only the target moves north, so the
 * room ahead fills the frame instead of camera-side rock. The prototype leads
 * along the camera's own axis (north), not the leader's facing, so a turn
 * never swings the frame. The camera follows the displayed position exactly,
 * with no smoothing (the prototype's phone-motion contract). The overview
 * modes wait for slice 4's controls.
 */
import * as THREE from 'three';
import { presentation } from './constants.ts';

const target = new THREE.Vector3();

/** How far north the target leads for this aspect ratio, in tiles. */
export const leadFor = (aspect: number): number => {
  const c = presentation.camera;
  return c.lead * Math.max(0, Math.min(1, (1 - aspect) / c.leadAspectRange));
};

/** Puts the camera on its follow pose for a leader displayed at game coordinates `(x, y)`; returns the target (world coordinates). */
export const follow = (camera: THREE.PerspectiveCamera, x: number, y: number): THREE.Vector3 => {
  const c = presentation.camera;
  const scale = Math.max(1, Math.min(c.portraitScaleMax, c.portraitAspect / camera.aspect));
  camera.position.set(x, c.targetY + (c.height * scale - c.targetY) * c.pull, -y + c.distance * scale * c.pull);
  target.set(x, c.targetY, -y - leadFor(camera.aspect));
  camera.lookAt(target);
  camera.updateMatrixWorld();
  return target;
};
